import React, { useState, useEffect, useCallback, useRef } from "react";
import usePersistedState from '../../hooks/usePersistedState';
import useDebounce from '../../hooks/useDebounce';
import Paginate from '../../components/Paginate';
import { itemsPerPage, formatPrice, handleApiError, DatePickerComponent } from '../../components/Utils';
import { Link } from 'react-router-dom';
import { FaSortUp, FaSortDown } from 'react-icons/fa';
import useOrderService from '../../hooks/useOrderService';
import useProviderService from '../../hooks/useProviderService';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Select,
    Button,
    MenuItem,
    Grid,
    FormControl,
    Autocomplete,
    InputLabel,
    CircularProgress,
    Typography
} from "@mui/material";
import dayjs from 'dayjs';
import "dayjs/locale/es";
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

function ListOrders() {
    const { getOrders } = useOrderService();
    const { getProviders } = useProviderService();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchReset, setFetchReset] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(null);

    const [orders, setOrders] = useState([]);

    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);

    const [minPrice, setMinPrice] = usePersistedState('list_orders_minPrice', null);
    const [maxPrice, setMaxPrice] = usePersistedState('list_orders_maxPrice', null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [code, setCode] = usePersistedState('list_orders_code', null);
    const [providerCode, setProviderCode] = usePersistedState('list_orders_providerCode', null);
    const [status, setStatus] = useState('');

    const debouncedCode = useDebounce(code);
    const debouncedProviderCode = useDebounce(providerCode);
    const debouncedMinPrice = useDebounce(minPrice);
    const debouncedMaxPrice = useDebounce(maxPrice);

    const [sortColumn, setSortColumn] = usePersistedState('list_orders_sortColumn', '');
    const [sortDirection, setSortDirection] = usePersistedState('list_orders_sortDirection', 'ASC');

    const isFirstFetch = useRef(true);

    const fetchOrders = useCallback(async () => {
        try {
            if (isFirstFetch.current) setLoading(true);
            setError(null);
            const params = {};
            params.limit = itemsPerPage;
            params.offset = (currentPage - 1) * itemsPerPage;
            if (!fetchReset) {
                if (minPrice) params.min_price = minPrice;
                if (maxPrice) params.max_price = maxPrice;
                if (selectedProvider) params.provider_id = selectedProvider.id;
                if (providerCode) params.provider_code = providerCode;
                if (code) params.code = code;
                if (status) params.order_status = status;
                if (sortColumn) params.sort_by = sortColumn;
                if (sortDirection) params.sort_direction = sortDirection;
                if (startDate) {
                    const startDateMidday = dayjs(startDate).hour(12).minute(0).second(0);
                    params.start_date = startDateMidday.utc().format('YYYY-MM-DD');
                }
                if (endDate) {
                    const endDateMidday = dayjs(endDate).hour(12).minute(0).second(0);
                    params.end_date = endDateMidday.utc().format('YYYY-MM-DD');
                }
            } else {
                setFetchReset(false);
            }
            const result = await getOrders(params);

            setOrders(result.data.items);
            setTotalItems(result.data.total);
            isFirstFetch.current = false;
        } catch (error) {
            setError(handleApiError(error, 'Error al obtener pedidos.'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, code, sortColumn, sortDirection, providerCode, minPrice, maxPrice, selectedProvider, startDate, endDate, status, fetchReset]);

    const fetchProviders = useCallback(async () => {
        try {
            const result = await getProviders();
            setProviders(result.data.items);
        } catch (error) {
            setError(handleApiError(error, 'Error al obtener proveedores.'));
        }
    }, []);

    useEffect(() => {
        fetchProviders();
    }, []);

    useEffect(() => {
        fetchOrders();
    }, [currentPage, sortColumn, sortDirection, fetchReset, debouncedCode, debouncedProviderCode, debouncedMinPrice, debouncedMaxPrice, selectedProvider, status, startDate, endDate]);

    const handleResetFilters = () => {
        setCurrentPage(1);
        setCode(null);
        setStartDate(null);
        setProviderCode(null);
        setEndDate(null);
        setStatus('');
        setSelectedProvider(null);
        setMaxPrice(null);
        setMinPrice(null);
        setFetchReset(true);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
        setCurrentPage(1);
    };

    const handleProviderChange = (event, newValue) => {
        setSelectedProvider(newValue);
    };
    const formatDate = (datetime) => {
        const date = new Date(datetime);
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return date.toLocaleDateString(undefined, options);
    };

    const handleStartDateChange = (newValue) => {
        setStartDate(newValue);
    };

    const handleEndDateChange = (newValue) => {
        setEndDate(newValue);
    };
    const handleStatusChange = (event) => {
        const { value } = event.target;
        setStatus(value);
    };
    const cellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '75px'
    };
    return (
        <div>
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Precio min"
                                        name="minPrice"
                                        value={minPrice || ""}
                                        onChange={(e) => setMinPrice(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Precio max"
                                        name="maxPrice"
                                        value={maxPrice || ""}
                                        onChange={(e) => setMaxPrice(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Código"
                                        name="code"
                                        value={code || ""}
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Código proporcionado por proveedor"
                                        name="provider_code"
                                        value={providerCode || ""}
                                        onChange={(e) => setProviderCode(e.target.value)}
                                    />
                                </Grid>
                                <Grid item sm={2}>
                                    <Autocomplete
                                        options={providers}
                                        getOptionLabel={(option) => option.name?.toString()}
                                        value={selectedProvider}
                                        onChange={handleProviderChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Proveedor"
                                                style={{ maxWidth: '100%' }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <FormControl fullWidth>
                                        <InputLabel id="status-label">Estado</InputLabel>
                                        <Select
                                            labelId="status-label"
                                            id="status"
                                            name="status"
                                            value={status}
                                            onChange={handleStatusChange}
                                            label="Tipo"
                                        >
                                            <MenuItem value="partly_received">PARCIALMENTE ACEPTADO</MenuItem>
                                            <MenuItem value="received">RECIBIDO</MenuItem>
                                            <MenuItem value="pending" >PENDIENTE</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item>
                                    <DatePickerComponent
                                        label="Fecha de Inicio"
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                        fullWidth={false}
                                    />
                                </Grid>
                                <Grid item>
                                    <DatePickerComponent
                                        label="Fecha de Fin"
                                        value={endDate}
                                        onChange={handleEndDateChange}
                                        fullWidth={false}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container spacing={1} justifyContent="flex-end">
                                <Grid item>
                                    <Button variant="contained" color="secondary" onClick={handleResetFilters}>
                                        Resetear Filtros
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>


                    <br />
                    <br />
                    <br />
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>
                                                Código {sortColumn === 'code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('provider_code')} style={{ cursor: 'pointer' }}>
                                                Código proporcionado por proveedor {sortColumn === 'provider_code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>Proveedor</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('order_status')} style={{ cursor: 'pointer' }}>
                                                Estado {sortColumn === 'order_status' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('order_date')} style={{ cursor: 'pointer' }}>
                                                Fecha {sortColumn === 'order_date' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('total_price')} style={{ cursor: 'pointer' }}>
                                                Precio total {sortColumn === 'total_price' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orders.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            <Link to={`/orders/update/${item.id}`}>
                                                {item.code}
                                            </Link>
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.provider_code}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.provider && item.provider.name ? (
                                                <Link to={`/providers/update/${item.provider.id}`}>
                                                    {item.provider.name} {item.provider.surname}
                                                </Link>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell component="th" scope="row" sx={cellStyle}>
                                            {item.order_status === 'partly_received' ? 'PARCIALMENTE RECIBIDO' :
                                                item.order_status === 'received' ? 'RECIBIDO' :
                                                    item.order_status === 'pending' ? 'PENDIENTE' :
                                                        item.order_status}
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            {formatDate(item.order_date)}
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            {formatPrice(item.total_price)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            <br />
            <br />
            <br />
            <Paginate
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
            />
            {error && (
                <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                    {error}
                </Typography>
            )}
        </div>
    )
}
export default ListOrders;
